import { useMemo } from 'react';

export const HOVER_HELP_CONTENT = {
    'dashboard.date-range': {
        title: 'Dashboard date range',
        body: 'Changes which dates feed the dashboard numbers. Choose Custom Range when you need a specific start and end date.',
    },
    'dashboard.edit-layout': {
        title: 'Edit dashboard',
        body: 'Lets you rearrange the dashboard and add or remove tiles. Save when the layout is right, or cancel to leave it unchanged.',
    },
    'dashboard.configure-widgets': {
        title: 'Configure widgets',
        body: 'Adds a custom formula, saved template, or quick metric tile to the dashboard while you are editing the layout.',
    },
    'dashboard.report-view-mode': {
        title: 'Report view',
        body: 'Summary keeps the report high level. Detailed shows the row-level records behind the numbers.',
    },
    'dashboard.report-export': {
        title: 'Export report',
        body: 'Downloads the report in the format you choose so it can be shared or reviewed outside the command center.',
    },
    'dashboard.custom-report-filters': {
        title: 'Report filters',
        body: 'Filters narrow the report before it runs. Multiple filters work together, so every condition must match.',
    },
    'dashboard.related-entity': {
        title: 'Related entity fields',
        body: 'Adds fields from the related account record to the report. Use it when the main report needs account details for context.',
    },
    'crm.clients.search': {
        title: 'Client search',
        body: 'Searches the visible client list by the client details on the card. Archived clients stay hidden unless the status filter includes them.',
    },
    'crm.clients.status-filter': {
        title: 'Client status filter',
        body: 'Limits the list to one client status. All statuses still hides archived clients unless Archived is selected directly.',
    },
    'crm.clients.view-mode': {
        title: 'Client view mode',
        body: 'Switches the same clients between List, Hybrid, and Card layouts. The choice is saved for your user profile.',
    },
    'crm.clients.booking-node': {
        title: 'Booking node',
        body: 'Chooses which business entity owns this client record when more than one booking node is available.',
    },
    'crm.clients.human-review': {
        title: 'Requires human review',
        body: 'Marks the client so staff should review details before automated handling goes too far, especially for VIP or sensitive accounts.',
    },
    'financials.transactions.search': {
        title: 'Transaction search',
        body: 'Finds transactions by visible details such as description, memo, amount, or category without changing the saved records.',
    },
    'financials.transactions.type-filter': {
        title: 'Transaction type filter',
        body: 'Shows income, expenses, or both. Use it to separate money coming in from money going out.',
    },
    'financials.transactions.category-filter': {
        title: 'Transaction category filter',
        body: 'Limits the table to one chart-of-accounts category so related transactions can be reviewed together.',
    },
    'financials.chart-view-mode': {
        title: 'Chart view mode',
        body: 'Dashboard View groups account categories into income and expense cards. Table View shows the same categories in a spreadsheet-style list.',
    },
    'financials.add-account-category': {
        title: 'Add account category',
        body: 'Creates a category used to organize transactions and reports, such as service revenue, fuel, or vehicle repairs.',
    },
    'communications.new-message': {
        title: 'New message',
        body: 'Starts a new conversation from the main pane. Search or type a recipient first, then choose the best available channel.',
    },
    'communications.thread-search': {
        title: 'Conversation search',
        body: 'Searches names, phone numbers, email addresses, and message text in the conversation list.',
    },
    'communications.thread-filters': {
        title: 'Conversation filters',
        body: 'Inbox chooses whose conversations you see. View chooses which list you see. Channel chooses which kind of message you see.',
    },
    'communications.thread-flags': {
        title: 'Thread status',
        body: 'Marks a conversation as follow-up, urgent, ASAP, needs attention, resolved, or closed so the team knows what needs action.',
    },
    'communications.compose-channel': {
        title: 'Send by',
        body: 'Chooses the outbound channel for a phone recipient. Pick SMS or WhatsApp before starting the thread.',
    },
    'communications.reply-channel': {
        title: 'Replying',
        body: 'Explains how replying works on the channel you picked.',
    },
    'communications.subject-line': {
        title: 'Subject line',
        body: 'Adds an optional subject to new messages when your Communications settings allow subjects.',
    },
    'communications.new-contact-handling': {
        title: 'New contact handling',
        body: 'Controls whether unknown recipients are saved automatically or whether staff choose to save or send without saving.',
    },
    'communications.thread-order': {
        title: 'Thread list order',
        body: 'Controls how conversations are sorted, grouped, and pinned so the busiest threads stay easy to find.',
    },
    'dispatch.queue-tabs': {
        title: 'Dispatch tabs',
        body: 'Switches between work waiting for assignment, past assignments, staff roster, and escalated intake cases.',
    },
    'dispatch.waterfall-legend': {
        title: 'Waterfall legend',
        body: 'Shows the dispatch phases a job can move through as offers are sent, accepted, and completed.',
    },
    'dispatch.assign-driver': {
        title: 'Assign driver',
        body: 'Opens the assignment form for a confirmed engagement that does not already have an active assignment.',
    },
    'dispatch.assignment-type': {
        title: 'Assignment type',
        body: 'Chooses whether the offer goes to an internal W-2 staff driver or a 1099 affiliate driver.',
    },
    'dispatch.assignment-history-filter': {
        title: 'Assignment history filter',
        body: 'Filters past assignment attempts by confirmation number, status, or type so dispatch can find a specific handoff quickly.',
    },
    'dispatch.affiliate-trip-link': {
        title: 'Affiliate trip status link',
        body: 'Creates or refreshes a no-login link an affiliate driver can use to send trip status taps such as en route, on site, and passenger onboard.',
    },
};

export function getHoverHelpContent(helpId, overrideContent) {
    if (overrideContent) return overrideContent;
    return HOVER_HELP_CONTENT[helpId] || null;
}

export function useHoverHelpContent(helpId, overrideContent) {
    return useMemo(() => getHoverHelpContent(helpId, overrideContent), [helpId, overrideContent]);
}
